import type { ListDiff } from './diffOrderedLists';
import { findCommonPrefixAndSuffix } from './findCommonPrefixAndSuffix';
import { longestCommonSubsequence } from './longestCommonSubsequence';
import type { DiffableArray } from './types';

/**
 * Skip the unchanged beginning and end, then diff only what is left in between.
 *
 * This is safe when used for _unique_ lists of document values because every value is unique: skipped
 * values can only match each other, so this does less work without changing the result.
 *
 * Provided 5% to 10% faster diffs when tested, performance gain depends mainly on whether there's
 * many unchanged values at the beginning or end of the list.
 *
 * @see https://www.gnu.org/software/diffutils/manual/html_node/diff-Performance.html
 */
export function diffOrderedUniqueLists<BeforeValue, AfterValue>(
    before: DiffableArray<BeforeValue>,
    after: DiffableArray<AfterValue>,
    // @ts-expect-error
    areEquals: (a: BeforeValue, b: AfterValue) => boolean = (a, b) => a === b
): Array<ListDiff<BeforeValue, AfterValue>> {
    // Preserve equal edges and only run the expensive comparison on the changed center.
    const { prefixLength, suffixLength } = findCommonPrefixAndSuffix(before, after, areEquals);

    if (prefixLength > 0 || suffixLength > 0) {
        const result: Array<ListDiff<BeforeValue, AfterValue>> = [];
        if (prefixLength > 0) {
            result.push({ op: '=', values: before.slice(0, prefixLength) });
        }
        result.push(
            ...diffOrderedUniqueLists(
                before.slice(prefixLength, before.length - suffixLength),
                after.slice(prefixLength, after.length - suffixLength),
                areEquals
            )
        );
        if (suffixLength > 0) {
            result.push({ op: '=', values: before.slice(before.length - suffixLength) });
        }
        return result;
    }

    // Use the longest shared sequence as an anchor for recursively diffing the remaining values.
    const commonSeq = longestCommonSubsequence(before, after, areEquals);

    const startBefore = commonSeq.start1;
    const startAfter = commonSeq.start2;

    // Without an anchor, every remaining value was either deleted or inserted.
    if (commonSeq.length === 0) {
        const result: Array<ListDiff<BeforeValue, AfterValue>> = [];
        if (before.length) {
            result.push({ op: '-', values: before.slice() });
        }
        if (after.length) {
            result.push({ op: '+', values: after.slice() });
        }
        return result;
    }

    const beforeLeft = before.slice(0, startBefore);
    const afterLeft = after.slice(0, startAfter);
    const beforeRight = before.slice(startBefore + commonSeq.length);
    const afterRight = after.slice(startAfter + commonSeq.length);

    // Diff both sides of the anchor, preserving the shared sequence between them.
    return [
        ...diffOrderedUniqueLists(beforeLeft, afterLeft, areEquals),
        {
            op: '=',
            values: before.slice(startBefore, startBefore + commonSeq.length),
        },
        ...diffOrderedUniqueLists(beforeRight, afterRight, areEquals),
    ];
}
